import React from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import transformOWASPData from "../DASTInsightsPages/charts/transformOWASPData";
import ProgressBarMUI from "../DASTInsightsPages/charts/ProgressBarMUI";
import InfoIcon from "@mui/icons-material/Info";

const tooltipTexts = {
  "Vulnerability Count":
    "Total number of code-level security flaws detected during the SAST scan. A lower value indicates better code hygiene.",
  "Vulnerability Distribution by Severity":
    "Proportion of identified vulnerabilities categorized as Critical, High, Medium, or Low severity based on SAST results.",
  "Vulnerability Density":
    "Number of vulnerabilities detected for every 1,000 lines of code. A lower value means cleaner, more secure code.",
  "False Positive Rate":
    "Percentage of reported issues that are incorrectly flagged as vulnerabilities. Lower rates indicate more accurate SAST scanning.",
  "True Positive Rate":
    "Percentage of correctly identified vulnerabilities in the total findings. Higher rates mean more reliable detection.",
  "Business Impact":
    "Degree to which identified code flaws could disrupt business operations if exploited. Higher scores signal greater business risk.",
  "Technical Impact":
    "Extent to which vulnerabilities affect the system’s technical performance, stability, or architecture.",
  "Return on Investment":
    "Estimated value gained from remediation efforts compared to the cost of addressing vulnerabilities.",
  "Compliance Met":
    "Indicates how well the codebase meets relevant compliance and regulatory security standards.",
  "Risk Rating":
    "Numeric score summarizing overall risk from detected SAST vulnerabilities. Lower values mean lower residual risk.",
};

// Function to get dynamic description based on risk weighted score
const getRiskWeightedDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Risk";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Risk";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Risk";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Risk";
  } else {
    return "Unknown Risk";
  }
};

// Function to get dynamic description based on business impact score
const getBusinessImpactDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Impact";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Impact";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Impact";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Impact";
  } else {
    return "Unknown Impact";
  }
};

// Helper to turn a technical-impact score into a label
const getTechnicalImpactDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Impact";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Impact";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Impact";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Impact";
  }
  return "Unknown Impact";
};

const SingleRunSecurityMetrics = ({ singleRunSecurityMetrics }) => {
  // Destructure all metrics from the passed prop
  const {
    vulnerabilityCount,
    vulnerabilityDistribution,
    vulnerabilityDensity,
    ROI,
    overallRiskRating,
    falsePositiveRate,
    truePositiveRate,
    businessImpactScore,
    technicalImpactScore,
    OWASPMetrics,
  } = singleRunSecurityMetrics;

  const owaspData = transformOWASPData(OWASPMetrics);
  const totalVulnerabilities = singleRunSecurityMetrics.vulnerabilityCount;

  // Format values for primary metrics
  const formatMetricValue = (metricName, value) => {
    if (typeof value !== "number") return value;

    switch (metricName) {
      case "Return on Investment":
        return value; // ROI is already a percentage string
      case "Average Risk Rating":
        return `${value.toFixed(1)}/10`;
      case "Vulnerability Density":
        return value.toFixed(1);
      default:
        return value;
    }
  };

  // Format values for secondary metrics
  const formatMetricValue2 = (metricName, value) => {
    if (typeof value !== "number") return value;

    switch (metricName) {
      case "False Positive Rate":
      case "True Positive Rate":
        return `${value.toFixed(1)}%`;
      case "Business Impact Score":
      case "Technical Impact Score":
        return `${value.toFixed(1)}/10`;
      default:
        return value.toFixed(1);
    }
  };

  const severityColors = {
    Critical: "#c00000",
    High: "#ff0000",
    Medium: "#ffc000",
    Low: "#39b549",
  };

  // Prepare pie chart data from vulnerabilityDistribution
  const pieData = Object.entries(vulnerabilityDistribution || {}).map(
    ([severity, val]) => ({
      name: severity,
      value: val,
      color: severityColors[severity] || "#000000",
    })
  );

  const MetricCard = ({
    title,
    value,
    subtitle,
    isLarge = true,
    tooltipText = "", // Single prop for tooltip text
  }) => {
    return (
      <Paper
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          textAlign: "center",
          backgroundColor: "#f8f9fa",
          height: isLarge ? "180px" : "100px",
          borderRadius: "8px",
          border: "1px solid #5391F7",
          justifyContent: "center",
          position: "relative", // Added for absolute positioning of tooltip
        }}
      >
        {/* Info Icon with Tooltip */}
        {tooltipText && (
          <Tooltip
            title={tooltipText}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 300, // Limit tooltip width for better readability
                  fontSize: "14px",
                  lineHeight: 1.4,
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                padding: "4px",
                color: "#5391F7",
                "&:hover": {
                  backgroundColor: "rgba(83, 145, 247, 0.1)",
                },
              }}
              aria-label="Info"
            >
              <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Card Content */}
        <Typography sx={{ fontWeight: 600, mb: 1, fontSize: "18px" }}>
          {title}
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: "27px", mb: 0.5 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: "15px", fontWeight: 500 }}>
            {subtitle}
          </Typography>
        )}
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "34px" }}>
          Security Metrics Analysis
        </Typography>
        {/* <Button
          sx={{
            backgroundColor: "#0148A2",
            color: "white",
            width: "209px",
            height: "40px",
            textTransform: "none",
            fontSize: "14px",
          }}
        >
          Single Run Metrics
        </Button> */}
      </Box>

      {/* Run 1 - Baseline Security Metrics */}
      <Box sx={{ mb: 3 }}>
        <Box>
          <Typography sx={{ fontWeight: 600, mb: 2, fontSize: "34px" }}>
            Run 1 - Baseline Security Metrics
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={3}>
              <MetricCard
                title="Vulnerability Count"
                value={formatMetricValue(
                  "Vulnerability Count",
                  vulnerabilityCount
                )}
                subtitle="Total number of True Positive vulnerabilities identified."
                tooltipText={tooltipTexts["Vulnerability Count"]}
              />
            </Grid>
            <Grid item xs={3}>
              <MetricCard
                title="Vulnerability Density"
                value={formatMetricValue(
                  "Vulnerability Density",
                  vulnerabilityDensity
                )}
                subtitle="TP vulnerabilities per 1000 LoC"
                tooltipText={tooltipTexts["Vulnerability Density"]}
              />
            </Grid>
            <Grid item xs={3}>
              <MetricCard
                title="Return on Investment"
                value={ROI}
                subtitle="Financial efficiency of security testing calculated from cost savings."
                tooltipText={tooltipTexts["Return on Investment"]}
              />
            </Grid>
            <Grid item xs={3}>
              <MetricCard
                title="Risk Rating"
                value={formatMetricValue(
                  "Average Risk Rating",
                  overallRiskRating
                )}
                subtitle={getRiskWeightedDescription(overallRiskRating)}
                tooltipText={tooltipTexts["Risk Rating"]}
              />
            </Grid>
          </Grid>

          {/* Vulnerability Distribution Pie Chart with labels on right */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mt: 4,
              border: "1px solid #5391F7",
              p: 3,
              borderRadius: "8px",
              position: "relative", // Added for tooltip positioning
            }}
          >
            {/* Title with Tooltip */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                position: "relative",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  mt: 4,
                  mb: 2,
                  fontSize: "23px",
                  flex: 1,
                  textAlign: "center",
                }}
              >
                Run 1 - Vulnerability Distribution by Severity
              </Typography>

              {/* Info Icon with Tooltip */}
              <Tooltip
                title={tooltipTexts["Vulnerability Distribution by Severity"]}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                      fontSize: "14px",
                      lineHeight: 1.4,
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: -10,
                    right: 8,
                    padding: "4px",
                    color: "#5391F7",
                    "&:hover": {
                      backgroundColor: "rgba(83, 145, 247, 0.1)",
                    },
                  }}
                  aria-label="Info"
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Rest of your existing code */}
            {pieData.every((d) => d.value === 0) ? (
              <Typography>No distribution data available.</Typography>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 4,
                  width: "100%",
                  maxWidth: 400,
                }}
              >
                <Box sx={{ width: "200px", height: "200px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mt: 2,
                    minWidth: 120,
                  }}
                >
                  {pieData.map((item) => (
                    <Box
                      key={item.name}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: item.color,
                          borderRadius: "10px",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          minWidth: "60px",
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", fontWeight: 400 }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Run 1 - Compliance Requirements Assessment */}
      <Box sx={{ mb: 3 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, mb: 2, fontSize: "34px" }}>
            Run 1 - Compliance Requirements Assessment
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={3}>
              <MetricCard
                title="False Positive Rate"
                value={formatMetricValue2(
                  "False Positive Rate",
                  falsePositiveRate
                )}
                subtitle="Percentage of False Positives over total findings."
                // subtitle={`Total FP: ${totalFalsePositives}`}
                isLarge={true}
                tooltipText={tooltipTexts["False Positive Rate"]}
              />
            </Grid>
            <Grid item xs={3}>
              <MetricCard
                title="True Positive Rate"
                value={formatMetricValue2(
                  "True Positive Rate",
                  truePositiveRate
                )}
                subtitle="Percentage of True Positives over total findings."
                // subtitle={`Total TP: ${totalTruePositives}`}
                isLarge={true}
                tooltipText={tooltipTexts["True Positive Rate"]}
              />
            </Grid>
            <Grid item xs={3}>
              <MetricCard
                title="Business Impact Score"
                value={formatMetricValue2(
                  "Business Impact Score",
                  businessImpactScore
                )}
                subtitle={getBusinessImpactDescription(businessImpactScore)}
                isLarge={true}
                tooltipText={tooltipTexts["Business Impact"]}
              />
            </Grid>
            <Grid item xs={3}>
              <MetricCard
                title="Technical Impact Score"
                value={formatMetricValue2(
                  "Technical Impact Score",
                  technicalImpactScore
                )}
                subtitle={getTechnicalImpactDescription(technicalImpactScore)}
                isLarge={true}
                tooltipText={tooltipTexts["Technical Impact"]}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* OWASP Top 10 Distribution */}
      <Grid
        container
        spacing={{ xs: 2, sm: 3 }}
        sx={{ mt: { xs: 2 }, mb: { xs: 4, md: 6 } }}
      >
        <Grid item xs={12}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "#5391F7",
              borderRadius: 2,
              p: { xs: 2, sm: 3 },
              height: "100%",
              position: "relative",
            }}
          >
            <Tooltip
              title={tooltipTexts["Compliance Met"]}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300, // Limit tooltip width for better readability
                    fontSize: "14px",
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>
            <Typography sx={{ fontSize: "23px", fontWeight: 400, mb: 2 }}>
              Vulnerability Distribution by OWASP Top 10
            </Typography>

            {owaspData.length > 0 ? (
              <Stack spacing={2}>
                {owaspData.map((item, i) => (
                  <ProgressBarMUI
                    key={i}
                    label={item.label}
                    value={item.value}
                    total={totalVulnerabilities}
                    color={item.color}
                  />
                ))}
              </Stack>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                No OWASP categorization data available
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SingleRunSecurityMetrics;
